import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { Button, Spinner, EmptyState, Paginator } from '../components/ui';
import toast from 'react-hot-toast';

const NOTIF_ICONS = {
  lead_assigned: '🎯',
  followup_due: '⏰',
  stage_changed: '🔄',
  task_due: '📋',
  mention: '💬',
  stale_lead: '💤',
  system: 'ℹ️',
};

const NOTIF_COLORS = {
  lead_assigned: { bg: '#eff6ff', icon: '#dbeafe', dot: '#3b82f6', border: '#3b82f6' },
  followup_due:  { bg: '#fff7ed', icon: '#ffedd5', dot: '#f97316', border: '#f97316' },
  stage_changed: { bg: '#f0fdf4', icon: '#dcfce7', dot: '#16a34a', border: '#16a34a' },
  task_due:      { bg: '#fefce8', icon: '#fef9c3', dot: '#ca8a04', border: '#ca8a04' },
  mention:       { bg: '#fdf4ff', icon: '#f3e8ff', dot: '#9333ea', border: '#9333ea' },
  stale_lead:    { bg: '#f8fafc', icon: '#f1f5f9', dot: '#94a3b8', border: '#94a3b8' },
  system:        { bg: '#f0f9ff', icon: '#e0f2fe', dot: '#0284c7', border: '#0284c7' },
};

const DEFAULT_COLOR = { bg: '#eff6ff', icon: '#dbeafe', dot: '#3b82f6', border: '#3b82f6' };

const PAGE_SIZE = 20;

export default function NotificationsPage() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await notificationsAPI.getAll({ all: 'true', page: p, limit: PAGE_SIZE });
      setNotifs(res.data?.data?.notifications || []);
      setTotalPages(res.data?.data?.totalPages || 1);
      setUnreadCount(res.data?.data?.unreadCount ?? 0);
      setPage(p);
    } catch { toast.error('Failed to load notifications'); } finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, []);

  const markRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifs(ns => ns.map(n => n._id === id ? { ...n, is_read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationsAPI.markAllRead();
      setNotifs(ns => ns.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed'); } finally { setMarkingAll(false); }
  };

  const handlePageChange = (p) => { load(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const timeAgo = (date) => {
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString('en-IN');
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>🔔 Notifications</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, margin: '4px 0 0' }}>
            {unreadCount > 0
              ? <><span style={{ fontWeight: 700, color: '#3b82f6' }}>{unreadCount} unread</span> · stay on top of your leads</>
              : 'You\'re all caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={markAllRead} disabled={markingAll}
            style={{ borderColor: '#e2e8f0', color: '#475569' }}>
            {markingAll ? 'Marking…' : '✓ Mark all read'}
          </Button>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spinner size={36} />
        </div>
      ) : notifs.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #e2e8f0', padding: '60px 24px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>All caught up!</p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>No notifications right now. Check back later.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
          {notifs.map((n, i) => {
            const colors = (n.is_read ? null : NOTIF_COLORS[n.type]) || DEFAULT_COLOR;
            return (
              <div key={n._id}
                onClick={() => !n.is_read && markRead(n._id)}
                style={{
                  display: 'flex',
                  gap: 14,
                  padding: '14px 20px',
                  background: n.is_read ? '#fff' : colors.bg,
                  borderBottom: i < notifs.length - 1 ? '1px solid #f1f5f9' : 'none',
                  borderLeft: `4px solid ${n.is_read ? 'transparent' : colors.border}`,
                  cursor: n.is_read ? 'default' : 'pointer',
                  transition: 'background 0.15s',
                }}>
                <div style={{
                  width: 40, height: 40, flexShrink: 0,
                  background: n.is_read ? '#f1f5f9' : colors.icon,
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {NOTIF_ICONS[n.type] || '🔔'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13.5, color: '#1e293b', fontWeight: n.is_read ? 400 : 600, lineHeight: 1.45, margin: 0 }}>
                    {n.message}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 5 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{timeAgo(n.createdAt)}</span>
                    {n.lead_id && (
                      <Link to={`/leads/${n.lead_id?._id || n.lead_id}`} onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: '#1B2A6B', textDecoration: 'none', fontWeight: 700, padding: '2px 8px', background: '#eff6ff', borderRadius: 6, border: '1px solid #dbeafe' }}>
                        View Lead →
                      </Link>
                    )}
                    {!n.is_read && (
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: colors.dot, display: 'inline-block', boxShadow: `0 0 0 2px ${colors.icon}` }} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {totalPages > 1 && (
            <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 20px' }}>
              <Paginator page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
